import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useAuthRedirect } from '../utils/authRedirect';
import AccountPanel from './AccountPanel';
import RemoveAdsPayment from './RemoveAdsPayment';
import { stripePromise } from '../utils/stripe';

function Profile({ isDarkMode, toggleDarkMode, onLogout }) {
    const [hasNoAdsState, setHasNoAdsState] = useState(null);
    const [paymentClientSecret, setPaymentClientSecret] = useState(null);
    const [paymentIntentId, setPaymentIntentId] = useState(null);
    const [showPaymentSheet, setShowPaymentSheet] = useState(false);
    const [isCreatingPayment, setIsCreatingPayment] = useState(false);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
    const [error, setError] = useState('');
    const [banner, setBanner] = useState('');
    const [localConsent, setLocalConsent] = useState('not set');

    const REMOVE_ADS_AMOUNT = 100;
    const REMOVE_ADS_CURRENCY = 'usd';

    const handleAuthError = useAuthRedirect();
    const { user, loading: userLoading, refreshUser, updateUser } = useUser();

    useEffect(() => {
        if (userLoading) {
            return;
        }

        if (user) {
            setHasNoAdsState(typeof user.hasNoAds === 'boolean' ? user.hasNoAds : false);
        } else {
            setHasNoAdsState(null);
        }
    }, [user, userLoading]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setLocalConsent(localStorage.getItem('ads_consent') || 'not set');
        }
    }, []);

    const handlePaymentPanelClose = useCallback(() => {
        setShowPaymentSheet(false);
        setPaymentClientSecret(null);
        setPaymentIntentId(null);
        setIsProcessingPayment(false);
        setIsCheckoutLoading(false);
    }, []);

    const handleRemoveAds = async () => {
        try {
            setError('');
            setBanner('');
            setIsCreatingPayment(true);
            const token = localStorage.getItem('token');
            const response = await fetch('/api/payment/create-payment-intent', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-auth-token': token,
                },
            });

            if (handleAuthError(response.status)) {
                return;
            }

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to start payment.');
            }

            const data = await response.json();
            setPaymentClientSecret(data.clientSecret);
            setPaymentIntentId(data.paymentIntentId);
            setShowPaymentSheet(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsCreatingPayment(false);
        }
    };

    const handleCheckoutFallback = useCallback(async () => {
        try {
            setError('');
            setIsCheckoutLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch('/api/payment/create-checkout-session', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-auth-token': token,
                },
            });

            if (handleAuthError(response.status)) {
                return;
            }

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to create checkout session.');
            }

            const session = await response.json();
            if (session.url) {
                window.location.href = session.url;
                return;
            }

            const publicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
            if (!publicKey) {
                throw new Error('Stripe public key is not configured.');
            }

            const resolvedStripe = await stripePromise;
            if (!resolvedStripe) {
                throw new Error('Stripe.js failed to load.');
            }

            const result = await resolvedStripe.redirectToCheckout({
                sessionId: session.id,
            });

            if (result?.error) {
                throw new Error(result.error.message);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setIsCheckoutLoading(false);
        }
    }, [handleAuthError]);

    const handlePaymentProcessingChange = useCallback((state) => {
        setIsProcessingPayment(state);
    }, []);

    const handlePaymentComplete = useCallback(async () => {
        let message = 'Payment successful! Ads have been removed.';
        try {
            if (paymentIntentId) {
                const token = localStorage.getItem('token');
                const resp = await fetch('/api/payment/confirm-intent', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-auth-token': token,
                    },
                    body: JSON.stringify({ paymentIntentId }),
                });

                if (handleAuthError(resp.status)) {
                    return;
                }

                if (resp.ok) {
                    const result = await resp.json();
                    if (typeof result.hasNoAds === 'boolean') {
                        setHasNoAdsState(result.hasNoAds);
                        updateUser({ hasNoAds: result.hasNoAds });
                        if (!result.hasNoAds) {
                            message = 'Payment succeeded. Ads will be removed shortly.';
                        }
                    }
                } else if (resp.status === 202) {
                    message = 'Payment is processing. Ads will disappear once Stripe confirms the charge.';
                } else {
                    const errData = await resp.json().catch(() => ({}));
                    message = errData.error
                        ? `Payment verified, but we could not update your profile automatically: ${errData.error}`
                        : 'Payment verified, but we could not update your profile automatically.';
                }
            }
        } catch (err) {
            message = `Payment received, but verification failed. Please refresh in a moment. (${err.message})`;
        }

        await refreshUser();
        setBanner(message);
        handlePaymentPanelClose();
    }, [paymentIntentId, refreshUser, handleAuthError, handlePaymentPanelClose, updateUser]);

    const isLoading = userLoading;

    return (
        <div className="page profile-page">
            <header className="page-header">
                <span className="page-eyebrow">Profile</span>
                <h1>Account settings</h1>
                <p className="page-subtitle">
                    Manage your account, payment status, and app preferences.
                </p>
                <div className="profile-actions">
                    <Link to="/saved-recipes" className="btn btn-outline-secondary">
                        Saved recipes
                    </Link>
                    <Link to="/app" className="btn btn-primary">
                        Workspace
                    </Link>
                </div>
            </header>

            {error && <div className="alert alert-danger" role="alert">{error}</div>}
            {banner && <div className="alert alert-info" role="alert">{banner}</div>}

            {isLoading ? (
                <p>Loading...</p>
            ) : (
                <div className="profile-layout">
                    <aside className="profile-sidebar">
                        <AccountPanel
                            user={user}
                            hasNoAdsState={hasNoAdsState}
                            onLogout={onLogout}
                        />

                        <div className="panel profile-panel">
                            <h5>Theme preference</h5>
                            <div className="form-check form-switch mt-3">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="darkModeSwitch"
                                    checked={isDarkMode}
                                    onChange={toggleDarkMode}
                                />
                                <label className="form-check-label" htmlFor="darkModeSwitch">
                                    {isDarkMode ? 'Dark mode' : 'Light mode'}
                                </label>
                            </div>
                        </div>

                        <div className="panel profile-panel mt-3">
                            <h5>Ad preferences</h5>
                            <p className="text-muted small">Manage whether ads are shown and your consent choice for personalized ads.</p>
                            <div className="d-flex gap-2 mt-2">
                                <button
                                    className="btn btn-outline-secondary"
                                    aria-label="Reject ads"
                                    onClick={async () => {
                                        try {
                                            localStorage.setItem('ads_consent', 'denied');
                                            setLocalConsent('denied');
                                            if (user && user._id) {
                                                const token = localStorage.getItem('token');
                                                await fetch('/api/ads/consent', {
                                                    method: 'POST',
                                                    headers: {
                                                        'Content-Type': 'application/json',
                                                        'x-auth-token': token,
                                                    },
                                                    body: JSON.stringify({ consent: 'denied' }),
                                                });
                                                updateUser({ adConsent: 'denied' });
                                            }
                                            setHasNoAdsState(typeof user?.hasNoAds === 'boolean' ? user.hasNoAds : false);
                                        } catch (err) {
                                            console.error('Failed to update consent', err);
                                        }
                                    }}
                                >Reject ads</button>

                                <button
                                    className="btn btn-primary"
                                    aria-label="Accept ads"
                                    onClick={async () => {
                                        try {
                                            localStorage.setItem('ads_consent', 'granted');
                                            setLocalConsent('granted');
                                            if (user && user._id) {
                                                const token = localStorage.getItem('token');
                                                await fetch('/api/ads/consent', {
                                                    method: 'POST',
                                                    headers: {
                                                        'Content-Type': 'application/json',
                                                        'x-auth-token': token,
                                                    },
                                                    body: JSON.stringify({ consent: 'granted' }),
                                                });
                                                updateUser({ adConsent: 'granted' });
                                            }
                                            setHasNoAdsState(typeof user?.hasNoAds === 'boolean' ? user.hasNoAds : false);
                                        } catch (err) {
                                            console.error('Failed to update consent', err);
                                        }
                                    }}
                                >Accept ads</button>

                                <button
                                    className="btn btn-link"
                                    aria-label="Reset ad consent"
                                    onClick={() => {
                                        if (typeof window !== 'undefined' && window.confirm('Reset ad consent? This will show onboarding again.')) {
                                            localStorage.removeItem('ads_consent');
                                            setLocalConsent('not set');
                                            // showing consent banner again is handled on next render / page load
                                        }
                                    }}
                                >Reset</button>
                            </div>
                            <div className="mt-2">
                                <span className="small text-muted">Current consent: </span>
                                <strong>{localConsent}</strong>
                                <div className="small text-muted">Consent captured during onboarding. You won't be shown an extra banner.</div>
                            </div>
                        </div>
                    </aside>

                    <main className="profile-content">
                        <div className="panel profile-panel">
                            <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                                <div>
                                    <h5>Plan and billing</h5>
                                    <p className="text-muted mb-0">
                                        Remove ads, confirm payment status, and manage your account in one place.
                                    </p>
                                </div>
                                <span className={`badge ${hasNoAdsState ? 'bg-success' : 'bg-warning text-dark'}`}>
                                    {hasNoAdsState ? 'Ad-free' : 'Ads active'}
                                </span>
                            </div>

                            <div className="profile-billing-grid mt-4">
                                <div className="profile-billing-card">
                                    <span className="account-stat__label">Status</span>
                                    <span className="account-stat__value">
                                        {hasNoAdsState === true && 'Ads removed'}
                                        {hasNoAdsState === false && 'Ads active'}
                                        {hasNoAdsState === null && 'Checking status…'}
                                    </span>
                                </div>
                                <div className="profile-billing-card">
                                    <span className="account-stat__label">Access</span>
                                    <span className="account-stat__value">Lifetime remove-ads unlock</span>
                                </div>
                            </div>

                            {hasNoAdsState === false && (
                                <div className="mt-4">
                                    <button
                                        onClick={handleRemoveAds}
                                        className="btn btn-primary"
                                        disabled={hasNoAdsState !== false || isCreatingPayment || isProcessingPayment || showPaymentSheet}
                                    >
                                        {isCreatingPayment || isProcessingPayment ? 'Preparing payment...' : 'Remove ads forever ($1.00)'}
                                    </button>
                                    <p className="text-muted small mt-2 mb-0">
                                        Pay once to unlock an ad-free experience. Wallets like Apple Pay and Google Pay are supported when available.
                                    </p>
                                </div>
                            )}

                            {showPaymentSheet && paymentClientSecret && hasNoAdsState === false && (
                                <div className="mt-4">
                                    <RemoveAdsPayment
                                        clientSecret={paymentClientSecret}
                                        amount={REMOVE_ADS_AMOUNT}
                                        currency={REMOVE_ADS_CURRENCY}
                                        onComplete={handlePaymentComplete}
                                        onCancel={handlePaymentPanelClose}
                                        onProcessingChange={handlePaymentProcessingChange}
                                        onCheckoutFallback={handleCheckoutFallback}
                                        isCheckoutLoading={isCheckoutLoading}
                                        isDarkMode={isDarkMode}
                                    />
                                </div>
                            )}
                        </div>
                    </main>
                </div>
            )}
        </div>
    );
}

export default Profile;
